import { useCallback, useEffect, useRef, useState } from 'react';

import type { PushRegistrar, PushRegistrationStatus } from './types';

export interface UseNotificationPreferences {
  loading: boolean;
  busy: boolean;
  error: unknown;
  status: PushRegistrationStatus | null;
  toggleEnabled(next?: boolean): Promise<void>;
  toggleCritical(next?: boolean): Promise<void>;
  refresh(): Promise<void>;
}

export function useNotificationPreferences(registrar: PushRegistrar): UseNotificationPreferences {
  const [status, setStatus] = useState<PushRegistrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async (op: () => Promise<PushRegistrationStatus>, kind: 'load' | 'op') => {
      if (kind === 'load') {
        setLoading(true);
      } else {
        setBusy(true);
      }
      setError(null);
      try {
        const next = await op();
        if (mounted.current) {
          setStatus(next);
        }
      } catch (e) {
        if (mounted.current) {
          setError(e);
        }
      } finally {
        if (mounted.current) {
          if (kind === 'load') {
            setLoading(false);
          } else {
            setBusy(false);
          }
        }
      }
    },
    [],
  );

  const refresh = useCallback(() => run(() => registrar.getStatus(), 'load'), [registrar, run]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const toggleEnabled = useCallback(
    async (next?: boolean) => {
      const target = next ?? !(status?.enabled ?? false);
      await run(() => (target ? registrar.enable() : registrar.disable()), 'op');
    },
    [registrar, run, status?.enabled],
  );

  const toggleCritical = useCallback(
    async (next?: boolean) => {
      const target = next ?? !(status?.criticalEnabled ?? false);
      await run(() => registrar.setCritical(target), 'op');
    },
    [registrar, run, status?.criticalEnabled],
  );

  return { loading, busy, error, status, toggleEnabled, toggleCritical, refresh };
}
